/**
 * BackgroundImageManager - Theme Background Image Configuration
 *
 * Allows users to upload custom background images for each theme.
 * Images are stored as base64 data URLs in localStorage.
 */

import * as React from 'react'
import { useRef } from 'react'
import { useTheme, themeVariants, type ThemeVariant } from '@/contexts/ThemeContext'
import { Button } from './Button'
import { X, Image, RotateCcw, Upload } from 'lucide-react'

interface BackgroundImageManagerProps {
  /** Whether the panel is open */
  isOpen: boolean
  /** Callback when panel should close */
  onClose: () => void
}

export function BackgroundImageManager({ isOpen, onClose }: BackgroundImageManagerProps) {
  const {
    variant: currentVariant,
    customBackgrounds,
    setCustomBackground,
    resetBackgroundToDefault,
  } = useTheme()

  // File input refs for each theme
  const fileInputRefs = useRef<Record<ThemeVariant, HTMLInputElement | null>>({
    'cp2077': null,
    'nebula-orange': null,
    'light-wispy': null,
  })

  if (!isOpen) return null

  const handleFileUpload = (theme: ThemeVariant, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB')
      return
    }

    // Convert to base64 data URL
    const reader = new FileReader()
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string
      setCustomBackground(theme, dataUrl)
    }
    reader.readAsDataURL(file)

    // Reset input
    if (fileInputRefs.current[theme]) {
      fileInputRefs.current[theme]!.value = ''
    }
  }

  const handleReset = (theme: ThemeVariant) => {
    resetBackgroundToDefault(theme)
  }

  const getCurrentImage = (theme: ThemeVariant): string | null => {
    return customBackgrounds[theme] || themeVariants[theme].defaultBgImage
  }

  const isCustom = (theme: ThemeVariant): boolean => {
    return customBackgrounds[theme] !== null
  }

  const themeKeys = Object.keys(themeVariants) as ThemeVariant[]

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative z-[101] w-full max-w-2xl max-h-[80vh] overflow-hidden rounded-[var(--theme-radius-lg)] bg-[var(--theme-surface)] border border-[var(--theme-glass-border-strong)] shadow-[var(--theme-glow-strong)]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--theme-glass-border)]">
          <div className="flex items-center gap-3">
            <Image className="w-5 h-5 text-[var(--theme-primary)]" />
            <h2 className="text-lg font-semibold text-[var(--theme-text-primary)]">
              Theme Background Images
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[var(--theme-glass-bg)] transition-colors"
          >
            <X className="w-5 h-5 text-[var(--theme-text-secondary)]" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
          {themeKeys.map((theme) => {
            const themeInfo = themeVariants[theme]
            const currentImage = getCurrentImage(theme)
            const hasCustom = isCustom(theme)
            const isActive = currentVariant === theme

            return (
              <div
                key={theme}
                className={`p-4 rounded-lg border transition-all ${
                  isActive
                    ? 'border-[var(--theme-primary)] bg-[var(--theme-glass-bg-heavy)]'
                    : 'border-[var(--theme-glass-border)] bg-[var(--theme-glass-bg)]'
                }`}
              >
                {/* Theme Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {/* Color swatch */}
                    <div
                      className="w-4 h-4 rounded-full border border-white/20"
                      style={{ backgroundColor: themeInfo.primaryColor }}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[var(--theme-text-primary)]">
                          {themeInfo.name}
                        </span>
                        {isActive && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--theme-primary)]/20 text-[var(--theme-primary)]">
                            Active
                          </span>
                        )}
                        {hasCustom && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
                            Custom
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-[var(--theme-text-tertiary)]">
                        {themeInfo.description}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {hasCustom && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReset(theme)}
                        title="Reset to default"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRefs.current[theme]?.click()}
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      Upload
                    </Button>
                    {/* Hidden file input */}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      ref={(el) => { fileInputRefs.current[theme] = el }}
                      onChange={(e) => handleFileUpload(theme, e)}
                    />
                  </div>
                </div>

                {/* Current Image Preview */}
                <div className="mb-3">
                  {currentImage ? (
                    <div
                      className="h-24 rounded-lg bg-cover bg-center bg-no-repeat border border-[var(--theme-glass-border)]"
                      style={{ backgroundImage: `url(${currentImage})` }}
                    />
                  ) : (
                    <div className="h-24 rounded-lg border border-dashed border-[var(--theme-glass-border)] flex items-center justify-center">
                      <span className="text-[var(--theme-text-tertiary)] text-sm">
                        No background image (solid color)
                      </span>
                    </div>
                  )}
                </div>

                {/* Status info */}
                <div className="text-xs text-[var(--theme-text-tertiary)]">
                  {hasCustom ? (
                    <>Custom image uploaded</>
                  ) : themeInfo.defaultBgImage ? (
                    <>Using default: {themeInfo.defaultBgImage}</>
                  ) : (
                    <>No background (solid color)</>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[var(--theme-glass-border)] bg-[var(--theme-glass-bg)]">
          <p className="text-xs text-[var(--theme-text-tertiary)] text-center">
            Upload an image (max 5MB). Changes are saved automatically and persist across sessions.
          </p>
        </div>
      </div>
    </div>
  )
}

export default BackgroundImageManager
